<template>
  <div class="agent-input">
    <!-- 内联提示条 -->
    <transition name="agent-tip-fade">
      <div v-if="inlineTip.visible" class="agent-input__tip" :class="`agent-input__tip--${inlineTip.type}`">
        <svg v-if="inlineTip.type === 'warn'" width="13" height="13" viewBox="0 0 24 24" fill="currentColor" style="flex-shrink:0">
          <path d="M12 2L1 21h22L12 2zm0 3.5L20.5 19h-17L12 5.5zM11 10v4h2v-4h-2zm0 6v2h2v-2h-2z"/>
        </svg>
        <svg v-else width="13" height="13" viewBox="0 0 24 24" fill="currentColor" style="flex-shrink:0">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
        </svg>
        <span>{{ inlineTip.message }}</span>
      </div>
    </transition>

    <!-- 附件预览列表 -->
    <div v-if="pendingAttachments.length > 0" class="agent-input__attachments">
      <div
        v-for="(att, idx) in pendingAttachments"
        :key="idx"
        :class="att.mimeType && att.mimeType.startsWith('image/') ? 'agent-input__attachment-img-item' : 'agent-input__attachment-item'"
      >
        <!-- 图片附件：缩略图 + hover 大图预览 -->
        <template v-if="att.mimeType && att.mimeType.startsWith('image/')">
          <div class="agent-input__img-thumb-wrap">
            <img
              :src="'data:' + att.mimeType + ';base64,' + att.data"
              class="agent-input__img-thumb"
              :alt="att.fileName"
            />
            <!-- hover 时浮出大图 -->
            <div class="agent-input__img-preview-popup">
              <img
                :src="'data:' + att.mimeType + ';base64,' + att.data"
                class="agent-input__img-preview-large"
                :alt="att.fileName"
              />
              <div class="agent-input__img-preview-name">{{ att.fileName }}</div>
            </div>
          </div>
          <button class="agent-input__attachment-remove agent-input__attachment-remove--img" @click="removeAttachment(idx)" title="移除">
            <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
            </svg>
          </button>
        </template>
        <!-- 文档附件：原标签样式 -->
        <template v-else>
          <span class="agent-input__attachment-icon">{{ resolveFileIcon(att.mimeType) }}</span>
          <span class="agent-input__attachment-name" :title="att.fileName">{{ att.fileName }}</span>
          <button class="agent-input__attachment-remove" @click="removeAttachment(idx)" title="移除">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/>
            </svg>
          </button>
        </template>
      </div>
    </div>

    <div class="agent-input__wrapper">
      <!-- 文件上传按钮 -->
      <button
        class="agent-input__attach-btn"
        :disabled="disabled || isGenerating || pendingAttachments.length >= 3"
        @click="triggerFileInput"
        :title="pendingAttachments.length >= 3 ? '最多同时上传 3 个文件' : '上传文件（PDF / Word / TXT / Excel / CSV / 图片，最多 3 个）'"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
      <!-- 隐藏的文件 input -->
      <input
        ref="fileInputRef"
        type="file"
        accept=".pdf,.doc,.docx,.txt,.xlsx,.xls,.csv,.jpg,.jpeg,.png,.gif,.webp,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv,image/*"
        multiple
        style="display:none"
        @change="handleFileChange"
      />

      <textarea
        ref="textareaRef"
        v-model="inputText"
        class="agent-input__textarea"
        placeholder="向 AI 助手提问..."
        rows="1"
        :disabled="disabled || isGenerating"
        @keydown.enter.exact.prevent="handleSend"
        @keydown.enter.shift.exact="handleNewLine"
        @compositionstart="isComposing = true"
        @compositionend="isComposing = false"
        @input="autoResize"
      ></textarea>
      <!-- 生成中：显示暂停按钮 -->
      <button
        v-if="isGenerating"
        class="agent-input__stop-btn"
        @click="handleStop"
        title="停止生成"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
          <rect x="4" y="4" width="16" height="16" rx="2"/>
        </svg>
      </button>
      <!-- 未生成：显示发送按钮 -->
      <button
        v-else
        class="agent-input__send-btn"
        :class="{ 'agent-input__send-btn--active': canSend }"
        :disabled="!canSend"
        @click="handleSend"
        title="发送（Enter）"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    </div>
    <div class="agent-input__hint">Enter 发送 · Shift+Enter 换行</div>
  </div>
</template>

<script>
import { ref, computed, nextTick } from '@vue/composition-api';

/** 支持的文件类型 */
const ACCEPTED_MIME_TYPES = new Set([
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  // Excel
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
  'application/vnd.ms-excel',                                          // .xls
  // CSV
  'text/csv',
  'application/csv',
  // 图片
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
]);

/** 单次最多上传文件个数 */
const MAX_FILE_COUNT = 3;

/** 按文件类型的大小上限 */
const FILE_SIZE_LIMITS = {
  'application/pdf': 10 * 1024 * 1024,                                                              // PDF: 10MB
  'application/msword': 5 * 1024 * 1024,                                                            // .doc: 5MB
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 5 * 1024 * 1024,       // .docx: 5MB
  'text/plain': 1 * 1024 * 1024,                                                                    // TXT: 1MB
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 10 * 1024 * 1024,            // .xlsx: 10MB
  'application/vnd.ms-excel': 10 * 1024 * 1024,                                                     // .xls: 10MB
  'text/csv': 5 * 1024 * 1024,                                                                      // CSV: 5MB
  'application/csv': 5 * 1024 * 1024,                                                               // CSV: 5MB
  'image/jpeg': 10 * 1024 * 1024,                                                                   // JPG: 10MB
  'image/png': 10 * 1024 * 1024,                                                                    // PNG: 10MB
  'image/gif': 10 * 1024 * 1024,                                                                    // GIF: 10MB
  'image/webp': 10 * 1024 * 1024,                                                                   // WebP: 10MB
};

function getFileSizeLimit(mimeType) {
  return FILE_SIZE_LIMITS[mimeType] ?? 10 * 1024 * 1024;
}

function formatFileSize(bytes) {
  if (bytes >= 1024 * 1024) return (bytes / 1024 / 1024).toFixed(0) + 'MB';
  return (bytes / 1024).toFixed(0) + 'KB';
}

export default {
  name: 'AgentMessageInput',
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    isGenerating: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['send', 'stop'],
  setup(props, { emit }) {
    const inputText = ref('');
    const textareaRef = ref(null);
    const fileInputRef = ref(null);
    const isComposing = ref(false);
    /** 待发送的附件列表：{ data: string, mimeType: string, fileName: string } */
    const pendingAttachments = ref([]);

    const canSend = computed(
      () =>
        !props.disabled &&
        !props.isGenerating &&
        (inputText.value.trim().length > 0 || pendingAttachments.value.length > 0),
    );

    /** 内联提示条状态 */
    const inlineTip = ref({ visible: false, type: 'warn', message: '' });
    let tipTimer = null;

    function showTip(message, type = 'warn') {
      if (tipTimer) clearTimeout(tipTimer);
      inlineTip.value = { visible: true, type, message };
      tipTimer = setTimeout(() => {
        inlineTip.value.visible = false;
      }, 3500);
    }

    function resolveFileIcon(mimeType) {
      if (mimeType === 'application/pdf') return '📄';
      if (mimeType === 'text/plain') return '📝';
      if (mimeType && mimeType.startsWith('image/')) return '🖼️';
      if (
        mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        mimeType === 'application/vnd.ms-excel' ||
        mimeType === 'text/csv' ||
        mimeType === 'application/csv'
      ) return '📊';
      return '📃'; // Word
    }

    function triggerFileInput() {
      if (props.disabled || props.isGenerating) return;
      fileInputRef.value && fileInputRef.value.click();
    }

    async function handleFileChange(event) {
      const files = Array.from(event.target.files || []);
      // 重置 input，允许重复选同一文件
      event.target.value = '';

      let skippedCount = 0;       // 因超出个数被截断的数量
      let typeErrors = [];         // 类型不支持的文件名
      let sizeErrors = [];         // 超大的文件名

      for (const file of files) {
        // 类型校验（优先，避免无意义的大小校验）
        const mimeType = file.type || resolveMimeByName(file.name);
        if (!ACCEPTED_MIME_TYPES.has(mimeType)) {
          typeErrors.push(file.name);
          continue;
        }
        // 大小校验（按类型分别限制）
        const sizeLimit = getFileSizeLimit(mimeType);
        if (file.size > sizeLimit) {
        const typeLabel = mimeType === 'text/plain' ? 'TXT'
          : mimeType.includes('pdf') ? 'PDF'
          : mimeType.startsWith('image/') ? '图片'
          : (mimeType.includes('spreadsheetml') || mimeType.includes('ms-excel')) ? 'Excel'
          : (mimeType === 'text/csv' || mimeType === 'application/csv') ? 'CSV'
          : 'Word';
          sizeErrors.push(`${file.name}（${typeLabel} 限 ${formatFileSize(sizeLimit)}，当前 ${formatFileSize(file.size)}）`);
          continue;
        }
        // 个数校验：超出时静默截断，记录数量
        if (pendingAttachments.value.length >= MAX_FILE_COUNT) {
          skippedCount++;
          continue;
        }
        // 读取为 base64
        const base64 = await readFileAsBase64(file);
        pendingAttachments.value.push({
          data: base64,
          mimeType,
          fileName: file.name,
        });
      }

      // 显示内联提示（优先级：类型错误 > 大小错误 > 个数截断）
      if (typeErrors.length > 0) {
        const names = typeErrors.slice(0, 2).join('、') + (typeErrors.length > 2 ? ` 等 ${typeErrors.length} 个` : '');
        showTip(`${names} 格式不支持，仅支持 PDF / Word / TXT / Excel / CSV / 图片`, 'error');
      } else if (sizeErrors.length > 0) {
        showTip(`文件过大：${sizeErrors[0]}`, 'error');
      } else if (skippedCount > 0) {
        showTip(`最多同时上传 ${MAX_FILE_COUNT} 个文件，已自动保留前 ${MAX_FILE_COUNT} 个`, 'warn');
      }
    }

    /** 根据文件名后缀推断 MIME（部分浏览器对 .doc/.xls 返回空） */
    function resolveMimeByName(name) {
      const lower = name.toLowerCase();
      if (lower.endsWith('.pdf')) return 'application/pdf';
      if (lower.endsWith('.docx')) return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      if (lower.endsWith('.doc')) return 'application/msword';
      if (lower.endsWith('.txt')) return 'text/plain';
      if (lower.endsWith('.xlsx')) return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      if (lower.endsWith('.xls')) return 'application/vnd.ms-excel';
      if (lower.endsWith('.csv')) return 'text/csv';
      if (lower.endsWith('.jpg') || lower.endsWith('.jpeg')) return 'image/jpeg';
      if (lower.endsWith('.png')) return 'image/png';
      if (lower.endsWith('.gif')) return 'image/gif';
      if (lower.endsWith('.webp')) return 'image/webp';
      return '';
    }

    function readFileAsBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          // result 格式：data:<mime>;base64,<data>，只取逗号后面的部分
          const result = e.target.result;
          const base64 = result.split(',')[1] || '';
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    }

    function removeAttachment(idx) {
      pendingAttachments.value.splice(idx, 1);
    }

    function handleSend() {
      if (isComposing.value) return;
      const text = inputText.value.trim();
      const attachments = pendingAttachments.value.slice();
      if ((!text && attachments.length === 0) || props.disabled || props.isGenerating) return;

      emit('send', text, attachments);

      inputText.value = '';
      pendingAttachments.value = [];
      nextTick(() => {
        const el = textareaRef.value;
        if (el) el.style.height = 'auto';
      });
    }

    function handleStop() {
      emit('stop');
    }

    function handleNewLine() {
      // Shift+Enter 换行，浏览器默认行为
    }

    function autoResize() {
      nextTick(() => {
        const el = textareaRef.value;
        if (!el) return;
        el.style.height = 'auto';
        const maxHeight = 120;
        el.style.height = Math.min(el.scrollHeight, maxHeight) + 'px';
      });
    }

    return {
      inputText,
      textareaRef,
      fileInputRef,
      canSend,
      isComposing,
      pendingAttachments,
      inlineTip,
      resolveFileIcon,
      triggerFileInput,
      handleFileChange,
      removeAttachment,
      handleSend,
      handleStop,
      handleNewLine,
      autoResize,
    };
  },
};
</script>

<style scoped>
.agent-input {
  padding: 10px 14px;
  background: #fff;
  border-top: 1px solid #eef0f3;
  flex-shrink: 0;
}

/* 附件预览列表 */
.agent-input__attachments {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
  align-items: flex-end;
}
/* 文档附件标签 */
.agent-input__attachment-item {
  display: flex;
  align-items: center;
  gap: 4px;
  background: #f0f4ff;
  border: 1px solid #c7d7ff;
  border-radius: 6px;
  padding: 3px 6px 3px 8px;
  max-width: 200px;
}
.agent-input__attachment-icon {
  font-size: 13px;
  flex-shrink: 0;
}
.agent-input__attachment-name {
  font-size: 12px;
  color: #2d4a8a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 130px;
}
.agent-input__attachment-remove {
  border: none;
  background: transparent;
  color: #8fa8d8;
  cursor: pointer;
  padding: 2px;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  border-radius: 3px;
  transition: color 0.15s, background 0.15s;
}
.agent-input__attachment-remove:hover {
  color: #e53e3e;
  background: #fff0f0;
}

/* 图片附件缩略图容器 */
.agent-input__attachment-img-item {
  position: relative;
  display: inline-flex;
  flex-shrink: 0;
}
.agent-input__img-thumb-wrap {
  position: relative;
  width: 64px;
  height: 64px;
  border-radius: 8px;
  overflow: visible;
  cursor: default;
  border: 1.5px solid #c7d7ff;
  background: #f0f4ff;
}
.agent-input__img-thumb {
  width: 64px;
  height: 64px;
  object-fit: cover;
  border-radius: 7px;
  display: block;
}
/* hover 浮出大图 */
.agent-input__img-preview-popup {
  display: none;
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.22);
  padding: 8px;
  z-index: 999;
  min-width: 160px;
  max-width: 280px;
  pointer-events: none;
}
.agent-input__img-thumb-wrap:hover .agent-input__img-preview-popup {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}
.agent-input__img-preview-large {
  max-width: 260px;
  max-height: 200px;
  border-radius: 6px;
  object-fit: contain;
  display: block;
}
.agent-input__img-preview-name {
  font-size: 11px;
  color: #86909c;
  max-width: 260px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: center;
}
/* 图片附件的删除按钮（右上角圆形） */
.agent-input__attachment-remove--img {
  position: absolute;
  top: -6px;
  right: -6px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 1.5px solid #fff;
  background: rgba(0,0,0,0.45);
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  z-index: 10;
  transition: background 0.15s;
}
.agent-input__attachment-remove--img:hover {
  background: #e53e3e;
}

/* 输入区域 */
.agent-input__wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f5f7fa;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  padding: 8px 8px 8px 8px;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.agent-input__wrapper:focus-within {
  border-color: #0052D9;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(0, 82, 217, 0.08);
}

/* 附件按钮 */
.agent-input__attach-btn {
  width: 30px;
  height: 30px;
  border-radius: 7px;
  border: none;
  background: transparent;
  color: #a0aec0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: color 0.2s, background 0.2s;
  padding: 0;
}
.agent-input__attach-btn:hover:not(:disabled) {
  color: #0052D9;
  background: #e8f0fe;
}
.agent-input__attach-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.agent-input__textarea {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  line-height: 1.6;
  color: #1a202c;
  resize: none;
  min-height: 22px;
  max-height: 120px;
  overflow-y: auto;
  font-family: inherit;
  padding: 0;
}
.agent-input__textarea::placeholder {
  color: #a0aec0;
}
.agent-input__textarea::-webkit-scrollbar {
  width: 3px;
}
.agent-input__textarea::-webkit-scrollbar-thumb {
  background: #cbd5e0;
  border-radius: 2px;
}
.agent-input__send-btn {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: none;
  background: #e2e8f0;
  color: #a0aec0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: not-allowed;
  flex-shrink: 0;
  transition: background 0.2s, color 0.2s, transform 0.1s;
}
.agent-input__send-btn--active {
  background: linear-gradient(135deg, #0052D9 0%, #2979FF 100%);
  color: #fff;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 82, 217, 0.3);
}
.agent-input__send-btn--active:hover {
  transform: scale(1.05);
}
.agent-input__send-btn--active:active {
  transform: scale(0.95);
}
/* 暂停按钮 */
.agent-input__stop-btn {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: 2px solid #0052D9;
  background: #fff;
  color: #0052D9;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.2s, color 0.2s, transform 0.1s;
  box-shadow: 0 2px 8px rgba(0, 82, 217, 0.15);
}
.agent-input__stop-btn:hover {
  background: #0052D9;
  color: #fff;
  transform: scale(1.05);
}
.agent-input__stop-btn:active {
  transform: scale(0.95);
}
.agent-input__hint {
  font-size: 11px;
  color: #c0c8d4;
  text-align: right;
  margin-top: 5px;
  padding-right: 2px;
}

/* 内联提示条 */
.agent-input__tip {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 7px;
  font-size: 12px;
  line-height: 1.5;
  margin-bottom: 6px;
}
.agent-input__tip--warn {
  background: #fffbe6;
  border: 1px solid #ffe58f;
  color: #875800;
}
.agent-input__tip--error {
  background: #fff2f0;
  border: 1px solid #ffccc7;
  color: #cf1322;
}
/* 提示条淡入淡出动画 */
.agent-tip-fade-enter-active,
.agent-tip-fade-leave-active {
  transition: opacity 0.25s, transform 0.25s;
}
.agent-tip-fade-enter,
.agent-tip-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
